import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, Send, ImageIcon, FileText, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ChatUI - shared chat panel used by buyer (/chat) and admin (/admin)
 *
 * Props:
 *  - threadId: string
 *  - currentSide: "buyer" | "admin"
 *  - title: header title
 *  - subtitle: header subtitle
 *  - fetchMessages(after?) -> Promise<{messages, thread}>
 *  - sendMessage({text, attachments}) -> Promise<message>
 *  - pollIntervalMs (default 3000)
 */
const ChatUI = ({
  threadId,
  currentSide,
  title,
  subtitle,
  fetchMessages,
  sendMessage,
  pollIntervalMs = 3000,
}) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);
  const pollRef = useRef(null);

  // Initial fetch
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetchMessages();
        if (!mounted) return;
        const msgs = res.messages || [];
        setMessages(msgs);
        if (msgs.length) lastTsRef.current = msgs[msgs.length - 1].created_at;
      } catch (e) {
        console.error(e);
        toast.error("Could not load chat");
      } finally {
        if (mounted) setLoadingInitial(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Polling
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetchMessages(lastTsRef.current || undefined);
        const incoming = res.messages || [];
        if (incoming.length) {
          setMessages((prev) => [...prev, ...incoming]);
          lastTsRef.current = incoming[incoming.length - 1].created_at;
        }
      } catch (e) {
        // silent
      }
    }, pollIntervalMs);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("File too large (max 12 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => [...prev, res.data]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed && attachments.length === 0) return;
    if (sending) return;
    setSending(true);
    try {
      const msg = await sendMessage({
        text: trimmed,
        attachments: attachments.map((a) => ({
          filename: a.filename,
          url: a.url,
          mime: a.mime,
          size: a.size,
        })),
      });
      setMessages((prev) => [...prev, msg]);
      lastTsRef.current = msg.created_at;
      setText("");
      setAttachments([]);
    } catch (e) {
      console.error(e);
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="flex flex-col h-full bg-[#0a0a0a] border border-[#262626]"
      data-testid="chat-panel"
    >
      {/* Header */}
      <div className="border-b border-[#262626] px-4 py-3 bg-[#050505] flex items-center justify-between">
        <div className="min-w-0">
          <div
            className="font-display text-xl tracking-wide uppercase truncate"
            data-testid="chat-title"
          >
            {title}
          </div>
          {subtitle && (
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717a] truncate">
              {subtitle}
            </div>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow shrink-0">
          ● Live
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-5 space-y-3"
        data-testid="chat-messages"
      >
        {loadingInitial && (
          <div className="text-center text-[#71717a] font-mono text-xs uppercase tracking-[0.25em]">
            Loading…
          </div>
        )}
        {!loadingInitial && messages.length === 0 && (
          <div className="text-center text-[#71717a] py-10 px-4">
            <div className="font-display text-2xl uppercase mb-2">No messages yet</div>
            <div className="font-mono text-xs tracking-[0.2em] uppercase">
              {currentSide === "buyer"
                ? "Say hi to Nick to receive your Blueprint"
                : "Waiting for the buyer to start the chat"}
            </div>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender === currentSide;
          return (
            <div
              key={m.id}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`msg-${m.sender}`}
            >
              <div
                className={`max-w-[80%] sm:max-w-[70%] px-4 py-2.5 border ${
                  mine
                    ? "bg-yellow text-[#050505] border-yellow"
                    : "bg-[#111111] text-white border-[#262626]"
                }`}
              >
                {m.text && (
                  <div className="whitespace-pre-wrap break-words leading-relaxed text-sm">
                    {m.text}
                  </div>
                )}
                {m.attachments && m.attachments.length > 0 && (
                  <div className={`mt-2 space-y-2 ${m.text ? "" : ""}`}>
                    {m.attachments.map((att, i) => {
                      const isImg = att.mime?.startsWith("image/");
                      const url = `${BACKEND_URL}${att.url}`;
                      if (isImg) {
                        return (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={url}
                              alt="attachment"
                              className="max-h-72 w-auto border border-black/20"
                              data-testid="msg-attachment-image"
                            />
                          </a>
                        );
                      }
                      return (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 px-2 py-1.5 border ${
                            mine
                              ? "border-black/30 hover:bg-black/10"
                              : "border-[#333] hover:bg-white/5"
                          }`}
                          data-testid="msg-attachment-file"
                        >
                          <FileText className="h-4 w-4 shrink-0" />
                          <span className="text-xs font-mono truncate">
                            {att.filename}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                )}
                <div
                  className={`mt-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
                    mine ? "text-black/60" : "text-[#71717a]"
                  }`}
                >
                  {formatTime(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Attachment preview */}
      {attachments.length > 0 && (
        <div className="border-t border-[#262626] px-3 py-2 flex flex-wrap gap-2 bg-[#0a0a0a]">
          {attachments.map((a, i) => (
            <div
              key={i}
              className="flex items-center gap-2 border border-yellow text-yellow px-2 py-1 text-xs font-mono"
              data-testid="attachment-preview"
            >
              {a.mime?.startsWith("image/") ? (
                <ImageIcon className="h-3 w-3" />
              ) : (
                <FileText className="h-3 w-3" />
              )}
              <span className="truncate max-w-[140px]">{a.original_name || a.filename}</span>
              <button
                onClick={() => removeAttachment(i)}
                className="text-yellow hover:text-white"
                aria-label="remove"
                data-testid="attachment-remove"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-[#262626] p-3 flex items-end gap-2 bg-[#050505]">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFile}
          className="hidden"
          data-testid="chat-file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="h-10 w-10 border border-[#333] text-[#a1a1aa] hover:text-yellow hover:border-yellow flex items-center justify-center shrink-0 transition"
          aria-label="attach"
          data-testid="chat-attach-btn"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <textarea
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={uploading ? "Uploading…" : "Type a message…"}
          className="flex-1 bg-[#0a0a0a] border border-[#262626] text-white placeholder:text-[#52525b] px-3 py-2 focus:outline-none focus:border-yellow resize-none font-sans text-sm"
          style={{ maxHeight: 120 }}
          data-testid="chat-text-input"
        />
        <button
          onClick={handleSend}
          disabled={sending || uploading || (!text.trim() && attachments.length === 0)}
          className="h-10 px-4 bg-yellow text-[#050505] font-display tracking-wide uppercase text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#e6c800] transition"
          data-testid="chat-send-btn"
        >
          <Send className="h-4 w-4" />
          Send
        </button>
      </div>
    </div>
  );
};

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default ChatUI;
